import { FC, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import axios from "shared/api/axiosConfig";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface UsageEntry {
  timestamp: string;
  model: keyof typeof pricing;
  input_tokens: number;
  output_tokens: number;
  instructions_tokens: number;
  total_tokens: number;
}

// Tarifs OpenAI (juillet 2025)
const pricing = {
  "gpt-4o": {
    input: 0.0025 / 1000,
    output: 0.01 / 1000
  },
  "gpt-3.5-turbo": {
    input: 0.0005 / 1000,
    output: 0.0015 / 1000
  },
  "gpt-4-1106-preview": {
    input: 0.01 / 1000,
    output: 0.03 / 1000
  },
  "gpt-4": {
    input: 0.03 / 1000,
    output: 0.06 / 1000
  }
};



const UsageDashboard: FC = () => {
  const [usageData, setUsageData] = useState<UsageEntry[]>([]);
  const [totalCost, setTotalCost] = useState<number>(0);

  const labels = usageData.map((entry) =>
    `${new Date(entry.timestamp).toLocaleTimeString()} (${entry.model})`
  );

  useEffect(() => {
    axios.get("/client/chat/stats").then((res) => {
      const data = res.data;
      let cumulative = 0;
      for (const entry of data) {
        const price = pricing[entry.model as keyof typeof pricing] || pricing["gpt-4o"];
        const inputCost =
          (entry.input_tokens + entry.instructions_tokens) * price.input;
        const outputCost = entry.output_tokens * price.output;
        cumulative += inputCost + outputCost;
      }
      setTotalCost(cumulative);
      setUsageData(data);
    });
  }, []);


  const inputTokens = usageData.map((e) => e.input_tokens);
  const outputTokens = usageData.map((e) => e.output_tokens);
  const instructionsTokens = usageData.map((e) => e.instructions_tokens);

  const costs = usageData.map((entry) => {
    const price = pricing[entry.model] || pricing["gpt-4o"];
    const inputCost =
      (entry.input_tokens + entry.instructions_tokens) * price.input;
    const outputCost = entry.output_tokens * price.output;
    return parseFloat((inputCost + outputCost).toFixed(4));
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Input Tokens",
        data: inputTokens,
        backgroundColor: "rgba(54, 162, 235, 0.6)"
      },
      {
        label: "Output Tokens",
        data: outputTokens,
        backgroundColor: "rgba(255, 99, 132, 0.6)"
      },
      {
        label: "Instructions Tokens",
        data: instructionsTokens,
        backgroundColor: "rgba(255, 206, 86, 0.6)"
      },
      {
        label: "Estimated Cost ($)",
        data: costs,
        backgroundColor: "rgba(75, 192, 192, 0.7)",
        yAxisID: "y1"
      }
    ]
  };

  const options = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Tokens"
        }
      },
      y1: {
        beginAtZero: true,
        position: "right" as const,
        title: {
          display: true,
          text: "Estimated Cost ($)"
        },
        grid: {
          drawOnChartArea: false
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      }
    }


  };

  

  return (
    <div className="container mt-10">
      <h2>📊 Token Usage & Cost Dashboard</h2>
      <p className="text-success">
        💰 <strong>Coût total cumulé :</strong> ${totalCost.toFixed(4)}
      </p>
      <Bar data={data} options={options} />


      <table className="table mt-10">
        <thead>
            <tr>
            <th>🕒 Heure</th>
            <th>🤖 Modèle</th>
            <th>📝 Instructions</th>
            <th>📥 Input</th>
            <th>📤 Output</th>
            <th>💡 Total Tokens</th>
            <th>💰 Coût Estimé ($)</th>
            </tr>
        </thead>
        <tbody>
            {usageData.map((entry, idx) => {
            const price = pricing[entry.model] || pricing["gpt-4o"];
            const inputCost =
                (entry.input_tokens + entry.instructions_tokens) * price.input;
            const outputCost = entry.output_tokens * price.output;
            const totalCost = inputCost + outputCost;

            return (
                <tr key={idx}>
                <td>{new Date(entry.timestamp).toLocaleTimeString()}</td>
                <td>{entry.model}</td>
                <td>{entry.instructions_tokens}</td>
                <td>{entry.input_tokens}</td>
                <td>{entry.output_tokens}</td>
                <td>{entry.total_tokens}</td>
                <td>${totalCost.toFixed(4)}</td>
                </tr>
            );
            })}
        </tbody>
    </table>
    </div>

    


  );
};

export default UsageDashboard;
